import { useMemo } from 'react';
import { useModules } from '../api/endpoints/useModules';
import type {
  ModuleItem,
  ModulesApiResponse,
} from '../api/endpoints/useModules';
import { useClientId } from './useClientId';

/**
 * Display label for the usecase page currently being rendered (DP-3570), or `undefined` when this
 * is not a usecase page or no matching nav leaf is visible.
 *
 * Sourced from the `/modules` payload rather than the `entityUsecase` registry, for three reasons:
 *
 * 1. The dashboard already has the modules payload, so this costs no extra request.
 * 2. FR-14 makes the leaf's own `label` an override of the registry record's label — so the leaf
 *    label is by definition the name the user clicked, which is what the header should echo.
 * 3. The registry is deliberately off the metadata hot path (FR-5), and reading it from the
 *    frontend would require every dashboard user to hold read scope on the registry entity.
 *
 * Known limitation: a usecase page reached by DEEP LINK renders even when the user's role has no
 * matching leaf (FR-20 / AC-10). There is no leaf to read in that case, so this returns undefined
 * and the caller falls back to the entity's schema title. Making the header identical in both cases
 * would require the backend to carry the label on the metadata response, which would put the
 * registry back on that path.
 */
export function useUsecaseLabel(
  entityName: string | undefined,
  usecase: string | undefined
): string | undefined {
  const clientId = useClientId();
  const { data } = useModules(clientId);

  return useMemo(() => {
    if (!entityName || !usecase) {
      return undefined;
    }
    return findUsecaseLeafLabel(data, entityName, usecase);
  }, [data, entityName, usecase]);
}

/**
 * Both module arrays as one flat root list. ModulesData is `ModulesConfig | {}` — an unconfigured
 * client sends `{}`, so guard on each field rather than assuming the config shape.
 */
function collectModuleRoots(
  data: ModulesApiResponse | undefined
): ModuleItem[] {
  const config = data?.data;
  return [
    ...(config && 'modules' in config ? config.modules : []),
    ...(config && 'bottomModules' in config
      ? (config.bottomModules ?? [])
      : []),
  ];
}

/**
 * True for an ENTITY leaf matching `(entityName, usecase)` exactly. Match on the pair rather than
 * the URL: the leaf carries both fields, so there is no string parsing to get wrong, and a
 * default-page leaf for the same entity can never match.
 */
function isMatchingUsecaseLeaf(
  item: ModuleItem,
  entityName: string,
  usecase: string
): boolean {
  return (
    'type' in item &&
    item.type === 'ENTITY' &&
    item.entityName === entityName &&
    item.usecase === usecase
  );
}

/** Walks both module arrays for an ENTITY leaf matching `(entityName, usecase)` exactly. */
function findUsecaseLeafLabel(
  data: ModulesApiResponse | undefined,
  entityName: string,
  usecase: string
): string | undefined {
  const stack: ModuleItem[] = collectModuleRoots(data);
  while (stack.length > 0) {
    const item = stack.pop();
    if (!item) {
      continue;
    }
    if (isMatchingUsecaseLeaf(item, entityName, usecase)) {
      return item.label;
    }
    if ('items' in item && Array.isArray(item.items)) {
      stack.push(...item.items);
    }
  }
  return undefined;
}
